import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { isString } from 'lodash';

import Payout from 'components/MobilePlacement/Payout/Payout';
import { PageBlocks } from 'constants/app';
import { BetslipTypes } from 'constants/betslip';
import { mobileComponents } from 'constants/branding';
import { InlinePlacementStatuses, MobilePlacementTypes } from 'constants/inlinePlacement';
import { BettingTypes } from 'constants/markets';
import { BetSides } from 'constants/myBets';
import { VALIDATION_ERROR_BET_OUTDATED_LINE, VALIDATION_ERROR_BET_OUTDATED_ODDS } from 'constants/placement';
import useConfirmBets from 'hooks/useConfirmBets';
import { useCurrency } from 'hooks/useCurrency';
import { useMarketUnits } from 'hooks/useMarketUnits';
import { useIsMobilePlacementDisabled } from 'hooks/useMobilePlacement';
import { usePlacementData } from 'hooks/usePlacement';
import usePostMessage from 'hooks/usePostMessage';
import {
  getBalanceWsEnabled,
  getCurrency,
  getGeneralWsEnabled,
  getIsLandscape,
  getIsOperatorBalanceEnabled,
  getPNCEnabledSetting
} from 'redux/modules/appConfigs/selectors';
import { getLoggedInStatusState } from 'redux/modules/auth/selectors';
import { setRGErrorMessage } from 'redux/modules/betslip';
import { setCurrentBetAction } from 'redux/modules/currentBets';
import { TCurrentBet } from 'redux/modules/currentBets/type';
import {
  failureInlinePlacedBet,
  removeInlineSelectedBet,
  removeProcessingMobileBet,
  setIsMobilePlacementConfirmationStep,
  setMobileInlineSelectedBetToRestore,
  setMobilePlacementNotification,
  setProcessingMobileBet,
  successInlinePlacedBet
} from 'redux/modules/inlinePlacement';
import { getIsMobilePlacementConfirmationStep } from 'redux/modules/inlinePlacement/selectors';
import { TInlineSelectedBet } from 'redux/modules/inlinePlacement/type';
import { getMarketDataById, getMarketPricesById } from 'redux/modules/placement/selectors';
import { TPlacementError } from 'redux/modules/placement/type';
import { fetchBalance } from 'redux/modules/user';
import { getUserAutoRefreshEnabled } from 'redux/modules/user/selectors';
import { PageBlock, PlacementPage } from 'types';
import { PersistenceType, TPrice, TProfit, TSize } from 'types/bets';
import { InlinePlacementStatus } from 'types/inlinePlacement';
import { getBestPriceFromMarketPrices, getBestPrices, isResponsibleGamblingError } from 'utils/betslip';
import { calculatePayout } from 'utils/liability';
import { getPriceMetadata } from 'utils/price';
import { getIsMaxProfitValid } from 'utils/profit';
import { getMaxSize } from 'utils/size';

import styles from './styles.module.scss';

type PlaceButtonProps = {
  bet: TInlineSelectedBet;
  priceValue: TPrice;
  prevPriceValue: TPrice;
  profit: TProfit;
  placedBet: TCurrentBet | null;
  sizeValue: TSize;
  persistenceType: PersistenceType;
  pageBlock: PageBlock;
  isTakeOffer: boolean;
  isSizeValid: boolean;
  isPriceReady: boolean;
  setPriceValue: (price: TPrice) => void;
  setIsTakeOffer: (isTakeOffer: boolean) => void;
  setHasErrorMessage: (hadError: boolean) => void;
  setStatus: (status: InlinePlacementStatus) => void;
  setErrorMessage: (message: string) => void;
  page?: PlacementPage;
};

const PlaceButton = ({
  bet,
  priceValue,
  prevPriceValue,
  profit,
  sizeValue,
  placedBet,
  persistenceType,
  isTakeOffer,
  isSizeValid,
  isPriceReady,
  setIsTakeOffer,
  setErrorMessage,
  setHasErrorMessage,
  setStatus,
  setPriceValue,
  page,
  pageBlock = PageBlocks.HOME
}: PlaceButtonProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const marketPrices = useSelector(getMarketPricesById(pageBlock, bet.marketId));
  const isConfirmationStep = useSelector(getIsMobilePlacementConfirmationStep);
  const isLandscape = useSelector(getIsLandscape);
  const { isDisabled } = useIsMobilePlacementDisabled(bet);
  const market = useSelector(getMarketDataById(pageBlock, bet.marketId, bet.sportId));
  const marketType = market?.description.marketType;
  const isPNCEnabled = useSelector(getPNCEnabledSetting);
  const autoRefreshIsEnabled = useSelector(getUserAutoRefreshEnabled);
  const defaultCurrency = useSelector(getCurrency);
  const isLoggedIn = useSelector(getLoggedInStatusState);
  const isOperatorBalanceEnabled = useSelector(getIsOperatorBalanceEnabled);
  const generalWsEnabled = useSelector(getGeneralWsEnabled);
  const balanceWsEnabled = useSelector(getBalanceWsEnabled);

  const { isConfirmBetsBeforePlacement } = useConfirmBets();
  const marketPrice = getBestPrices({ marketPrices, ...bet });
  const bettingType = market?.description.bettingType;
  const bestPrice = getBestPriceFromMarketPrices(marketPrice, marketType ?? '', bettingType ?? '');
  const isPrevOddsLower = !isDisabled && !!(bet.price && +bet.price < (bestPrice || 0));
  const isBack = bet.type === BetSides.Back;
  const isGoingUp = !isDisabled && isBack ? isPrevOddsLower : !isPrevOddsLower;
  const isGame = pageBlock === PageBlocks.GAME;
  const lineRangeInfo = market?.description?.lineRangeInfo;
  const marketUnit = lineRangeInfo?.marketUnit || 'runs';
  const isOddsChangedIndicatorsEnabled =
    isPNCEnabled &&
    autoRefreshIsEnabled &&
    !isGame &&
    !!bet.price &&
    !!bestPrice &&
    bet.price !== bestPrice &&
    !isDisabled;
  const isLowerPrice = isOddsChangedIndicatorsEnabled && !isGoingUp;
  const isLineMarket = bettingType === BettingTypes.LINE;
  const marketUnitTranslated = useMarketUnits(marketUnit);
  const showBorder = isConfirmationStep || isLowerPrice;
  const prevSizeValue = useRef<TSize>(bet.size);
  const currentPrice = !isConfirmationStep && autoRefreshIsEnabled && isPNCEnabled && !isGame ? bestPrice : priceValue;

  const payout = calculatePayout(currentPrice, sizeValue, {
    marketType,
    bettingType,
    eachWayDivisor: bet.eachWayDivisor,
    betType: bet.type
  });

  const { placeBetLogin } = usePostMessage();

  const currency = useCurrency(defaultCurrency.currencyCode);
  const priceLadderDescription = market?.description.priceLadderDescription;

  const onSuccessPlacement = (placedBets: TCurrentBet[], cancelledBets: TCurrentBet[]) => {
    if (placedBets.length) {
      dispatch(successInlinePlacedBet({ ...bet, placedBet: placedBets[0] }));
      if (!isOperatorBalanceEnabled && (!generalWsEnabled || !balanceWsEnabled)) {
        dispatch(fetchBalance());
      }
    }

    if (cancelledBets.length) {
      dispatch(setCurrentBetAction({ offerId: cancelledBets[0].offerId, action: null }));
    }
  };

  const onCancelledPlacement = () => {
    if (isPNCEnabled && !isGame) {
      setIsTakeOffer(true);
      setErrorMessage(
        isLineMarket
          ? t(VALIDATION_ERROR_BET_OUTDATED_LINE, { unit: marketUnitTranslated })
          : t(VALIDATION_ERROR_BET_OUTDATED_ODDS)
      );
      setStatus(InlinePlacementStatuses.CONFIRM);
    }
  };

  const onErrorPlacement = (error: TPlacementError | string = '') => {
    const newErrorMessage = isString(error) ? error : error?.response?.data?.message ?? '';

    setErrorMessage(newErrorMessage);
    setStatus(InlinePlacementStatuses.NEW);
    dispatch(failureInlinePlacedBet({ ...bet, error }));
    setHasErrorMessage(true);

    if (isResponsibleGamblingError(error)) {
      dispatch(setRGErrorMessage(error));
    }

    if (placedBet) {
      setStatus(InlinePlacementStatuses.PLACED_EDIT);
      dispatch(removeProcessingMobileBet(placedBet.offerId));
    } else {
      setStatus(InlinePlacementStatuses.NEW);
    }
  };

  const placement = usePlacementData({
    eachWayDivisor: market?.description?.eachWayDivisor,
    numberOfWinners: market?.numberOfWinners,
    successPlacement: onSuccessPlacement,
    errorPlacement: onErrorPlacement,
    onCancelledPlacement,
    isMobilePlacement: true
  });

  const getPlaceButtonLabel = () => {
    if (isConfirmationStep) {
      return t('inlinePlacement.labels.confirmBet');
    } else if (isLowerPrice && !isLineMarket) {
      return t('placement.labels.acceptOdds');
    } else if (isTakeOffer) {
      return isLineMarket
        ? t('betslip.actions.placeBetAtAvailableUnits', { unit: marketUnitTranslated })
        : t('betslip.actions.placeBetAtAvailableOdds');
    } else if (bet.currentOfferId) {
      return t('inlinePlacement.labels.updateBet');
    } else {
      return t('inlinePlacement.labels.placeBet');
    }
  };

  const getIsPlacementBtnEnabled = () => {
    const { max: maxPrice, min: minPrice } = getPriceMetadata({
      price: priceValue,
      marketType: marketType || '',
      bettingType: bettingType || '',
      lineRangeInfo,
      priceLadderDescription
    });

    let isEnabled = false;

    const isMaxProfitValid = getIsMaxProfitValid({
      profit,
      currency,
      defaultCurrency,
      isGame
    });
    const maxSize = getMaxSize({ currency, defaultCurrency, isGame, isLay: !isBack }).max;

    if (
      !isDisabled &&
      priceValue &&
      Number(priceValue) >= minPrice &&
      Number(priceValue) <= maxPrice &&
      sizeValue &&
      !isNaN(+sizeValue) &&
      Number(sizeValue) <= maxSize &&
      Number(sizeValue) >= currency.minBetSize &&
      (!isBack || isMaxProfitValid)
    ) {
      if (bet.currentOfferId) {
        isEnabled =
          Number(sizeValue) !== Number(prevSizeValue.current) ||
          Number(priceValue) !== Number(prevPriceValue) ||
          (!!placedBet && placedBet.persistenceType !== persistenceType);
      } else {
        isEnabled = true;
      }
    }

    return isEnabled;
  };

  const isPlacementBtnEnabled = getIsPlacementBtnEnabled();

  const isBtnDisabled = !isPlacementBtnEnabled || !isSizeValid || !isPriceReady;

  const confirmBet = () => {
    if (isLoggedIn) {
      const betType = isGame ? BetslipTypes.GAME : BetslipTypes.EXCHANGE;
      const options = {
        isTakeOffer,
        betType,
        pageBlock,
        ...(isGame && market?.round ? { round: market?.round } : {})
      };
      setStatus(InlinePlacementStatuses.PROGRESS);
      setHasErrorMessage(false);

      if (placedBet) {
        dispatch(
          setMobilePlacementNotification({
            pageBlock,
            data: {
              marketId: placedBet.marketId,
              offerId: placedBet.offerId,
              betUuid: bet.betUuid!,
              priceToShow: currentPrice,
              sizeToShow: sizeValue
            },
            placementType: MobilePlacementTypes.Edit
          })
        );

        dispatch(setProcessingMobileBet({ offerIdOrBetUuid: placedBet.offerId, isLoading: true }));

        placement.editBetsHandler({
          marketId: bet.marketId,
          bets: [
            {
              price: currentPrice,
              size: sizeValue,
              side: placedBet.side,
              selectionId: placedBet.selectionId,
              handicap: placedBet.handicap,
              offerId: placedBet.offerId,
              sizeRemaining: placedBet.sizeRemaining,
              persistenceType,
              page,
              betUuid: bet.betUuid
            }
          ],
          options
        });
      } else {
        dispatch(
          setMobilePlacementNotification({
            pageBlock,
            data: {
              marketId: bet.marketId,
              betUuid: bet.betUuid!,
              bet
            },
            placementType: MobilePlacementTypes.Place
          })
        );

        if (bet.betUuid) {
          dispatch(setProcessingMobileBet({ offerIdOrBetUuid: bet.betUuid, isLoading: true }));
        }

        placement.placeBetsHandler({
          bets: [
            {
              marketId: bet.marketId,
              price: currentPrice,
              size: sizeValue,
              handicap: bet.handicap,
              selectionId: bet.selectionId,
              side: bet.type.toUpperCase(),
              page,
              persistenceType,
              betUuid: bet.betUuid
            }
          ],
          options
        });
      }

      dispatch(removeInlineSelectedBet(bet));
      dispatch(setMobileInlineSelectedBetToRestore(bet));
    } else {
      placeBetLogin();
    }
  };

  const onConfirmBtnClick = () => {
    if (isPlacementBtnEnabled && isSizeValid) {
      if (isConfirmBetsBeforePlacement && !isConfirmationStep && !bet.currentOfferId) {
        dispatch(setIsMobilePlacementConfirmationStep(true));
        if (autoRefreshIsEnabled && isPNCEnabled && !isGame) {
          setPriceValue(bestPrice);
        }
      } else {
        confirmBet();
      }
    }
  };

  return (
    <button
      onClick={onConfirmBtnClick}
      type="button"
      disabled={isBtnDisabled}
      className={classNames(styles.placeBtn, mobileComponents.BUTTON, mobileComponents.PRIMARY, {
        [styles.placeBtn__confirm]: showBorder || isTakeOffer,
        biab_disabled: isBtnDisabled,
        [styles.landscape__placeBtn]: isLandscape,
        [mobileComponents.FOCUS]: showBorder && !isBtnDisabled,
        [mobileComponents.DISABLED]: isBtnDisabled
      })}
    >
      {getPlaceButtonLabel()}
      {isLandscape && <Payout payout={payout} isLandscape />}
    </button>
  );
};

export default PlaceButton;
